/**
 * brochurePdfShared.js — Helpers comuni a tutti i layout vector brochure.
 *
 * Estratti da brochureVectorPDF.js per condividerli tra Elegante / Luxury /
 * Moderno / Minimal senza duplicazione.
 */

// A4 portrait in mm
export const A4_W = 210;
export const A4_H = 297;

// ─── Photo loader ──────────────────────────────────────────────────
/**
 * Scarica una immagine da URL e la converte in dataURL (JPEG o PNG).
 *
 * iter v28 r2: Usa fetch+blob→FileReader invece di <img crossOrigin>.
 * Motivo: i URL del manifest contengono auth in query param ed `<img>`
 * cross-origin spesso hangs o fallisce silenziosamente per timing/CORS.
 * Con fetch abbiamo timeout esplicito + accesso al blob senza re-encoding.
 *
 * Returns: {dataUrl, w, h} o null se fallisce.
 */
export async function loadImageAsJpegDataUrl(url, maxWidth = 1600, opts = {}) {
  if (!url) return null;

  // iter v28 r17 — Auto-detect logo per preservare alpha (PNG output):
  //   • URL contenente /logo o logo.png  → matcha pattern legacy
  //   • opts.forcePng = true            → forzato dal chiamante (es. loadAgencyLogo)
  //   • opts.tint                       → quando si tinge serve obbligatoriamente
  //                                        PNG output per non perdere l'alpha mask
  const wantPng = opts.forcePng === true
    || !!opts.tint
    || /\/logo($|\?|\.)|logo\.png|agency_logo|brand/i.test(url);
  // iter v28 r7: tint opzionale per logo monocromatici.
  // opts.tint = "white" | "black" → applica un color overlay preservando alpha.
  const tint = opts.tint;

  // 1) Fetch blob con timeout
  const ctrl = new AbortController();
  const tid = setTimeout(() => ctrl.abort(), 8000);
  let blob;
  try {
    const resp = await fetch(url, { credentials: "include", signal: ctrl.signal });
    if (!resp.ok) {
      clearTimeout(tid);
      console.warn("[brochure] photo fetch !ok", resp.status, url.slice(0, 80));
      return null;
    }
    blob = await resp.blob();
    if (!blob || blob.size === 0) {
      clearTimeout(tid);
      console.warn("[brochure] photo blob empty", url.slice(0, 80));
      return null;
    }
  } catch (e) {
    clearTimeout(tid);
    console.warn("[brochure] photo fetch err", e?.message || e, url.slice(0, 80));
    return null;
  }
  clearTimeout(tid);

  // 2) Blob → ObjectURL → Image → Canvas (locale, no CORS issues)
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    const cleanup = () => {
      try { URL.revokeObjectURL(objectUrl); } catch (_) {}
    };
    const failTimer = setTimeout(() => {
      cleanup();
      resolve(null);
    }, 6000);

    img.onload = () => {
      clearTimeout(failTimer);
      try {
        const scale = Math.min(1, maxWidth / img.naturalWidth);
        const w = Math.round(img.naturalWidth * scale);
        const h = Math.round(img.naturalHeight * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!wantPng) {
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, w, h);
        }
        ctx.drawImage(img, 0, 0, w, h);

        // iter v28 r7: tint del logo monocromatico per matcharlo al colore
        // dei testi del PDF (es. logo nero su brochure scura → tinta bianca).
        // Usiamo source-in: il canale alpha del logo originale fa da maschera,
        // tutti i pixel diventano del colore desiderato.
        if (tint === "white" || tint === "black") {
          ctx.globalCompositeOperation = "source-in";
          ctx.fillStyle = tint === "white" ? "#FFFFFF" : "#000000";
          ctx.fillRect(0, 0, w, h);
          ctx.globalCompositeOperation = "source-over";
        }

        const dataUrl = wantPng
          ? canvas.toDataURL("image/png")
          : canvas.toDataURL("image/jpeg", 0.92);
        cleanup();
        resolve({ dataUrl, w, h });
      } catch (e) {
        cleanup();
        resolve(null);
      }
    };
    img.onerror = () => {
      clearTimeout(failTimer);
      cleanup();
      resolve(null);
    };
    img.src = objectUrl;
  });
}

// ─── Cover-fit photo placer ────────────────────────────────────────
/**
 * iter v28 r16 — Fix immagini "schiacciate o allungate" nelle brochure PDF.
 *
 * Prima: `pdf.addImage(photo.dataUrl, "JPEG", x, y, w, h, ..., "MEDIUM")`
 *        → jsPDF stira la foto sulle dimensioni esatte della box, ignorando
 *        l'aspect ratio originale. Risultato: foto orizzontali compresse,
 *        verticali allungate, soggetti deformati. Inoltre compressione
 *        "MEDIUM" introduce blur visibili in PDF stampati.
 *
 * Adesso: `await addPhotoCover(pdf, photo, x, y, w, h)` fa cover-fit
 *        (center-crop) PRIMA di passare la foto a jsPDF. La data-url
 *        risultante ha esattamente l'aspect ratio della box → no più
 *        stiramenti. La risoluzione del canvas target è calcolata a 300 DPI
 *        (qualità stampa) → no più blocchi sgranati. Compressione "FAST"
 *        (DCT con quality 95) → ~25% file più grande ma nitidezza visibile.
 *
 * @param {object}        pdf      — istanza jsPDF
 * @param {object|null}   photo    — {dataUrl, w, h} da loadImageAsJpegDataUrl
 * @param {number}        xMm,yMm  — top-left della box in mm
 * @param {number}        wMm,hMm  — dimensioni box in mm
 * @param {object}        [opts]   — {dpi=300, quality=0.95, anchor='center'}
 *                                  anchor: 'center'|'top'|'bottom' per il
 *                                  crop verticale (utile su volti).
 * @returns {Promise<boolean>}     — true se piazzata, false se photo null/err.
 */
export async function addPhotoCover(pdf, photo, xMm, yMm, wMm, hMm, opts = {}) {
  if (!photo || !photo.dataUrl) return false;
  const dpi = opts.dpi || 300;
  const quality = opts.quality ?? 0.95;
  const anchor = opts.anchor || "center";

  const targetPxW = Math.max(1, Math.round((wMm * dpi) / 25.4));
  const targetPxH = Math.max(1, Math.round((hMm * dpi) / 25.4));

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const srcW = img.naturalWidth || photo.w;
        const srcH = img.naturalHeight || photo.h;
        const srcAspect = srcW / srcH;
        const tgtAspect = targetPxW / targetPxH;

        // Cover-fit: prendiamo la massima area centrale del source che
        // matcha l'aspect ratio della box di destinazione.
        let cropW, cropH, cropX, cropY;
        if (srcAspect > tgtAspect) {
          // Sorgente più larga della box → tagliamo i lati
          cropH = srcH;
          cropW = Math.round(srcH * tgtAspect);
          cropX = Math.round((srcW - cropW) / 2);
          cropY = 0;
        } else {
          // Sorgente più alta della box → tagliamo testa/piedi
          cropW = srcW;
          cropH = Math.round(srcW / tgtAspect);
          cropX = 0;
          if (anchor === "top") cropY = 0;
          else if (anchor === "bottom") cropY = srcH - cropH;
          else cropY = Math.round((srcH - cropH) / 2);
        }

        const canvas = document.createElement("canvas");
        canvas.width = targetPxW;
        canvas.height = targetPxH;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, targetPxW, targetPxH);
        ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, targetPxW, targetPxH);

        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        // "FAST" in jsPDF = minore ricompressione lato PDF → preserva la
        // qualità del nostro JPEG di partenza.
        pdf.addImage(dataUrl, "JPEG", xMm, yMm, wMm, hMm, undefined, "FAST");
        resolve(true);
      } catch (e) {
        console.warn("[brochure] addPhotoCover failed", e);
        resolve(false);
      }
    };
    img.onerror = () => resolve(false);
    img.src = photo.dataUrl;
  });
}

// ─── Aspect-ratio-preserving logo placer ──────────────────────────────
/**
 * iter v28 r17 — Posiziona un logo dentro una box mantenendone l'aspect ratio
 * (no stretching). Allinea center/start/end via opts.align.
 *
 * Prima problema: nei footer brochure faccio `pdf.addImage(logo.dataUrl, "PNG",
 * x, y, 12, 8)` ma il logo era 200×200 → diventava 12×8 = orizzontalmente
 * schiacciato. Adesso fit-contain.
 *
 * @param {object} pdf      — istanza jsPDF
 * @param {object} logo     — {dataUrl, w, h}
 * @param {number} xMm,yMm  — top-left box mm
 * @param {number} maxWMm   — larghezza max mm
 * @param {number} maxHMm   — altezza max mm
 * @param {object} [opts]   — {align: "center"|"left"|"right"}
 * @returns {{w, h, x, y}}  — dimensioni effettive piazzate
 */
export function addLogoFit(pdf, logo, xMm, yMm, maxWMm, maxHMm, opts = {}) {
  if (!logo || !logo.dataUrl) return null;
  const align = opts.align || "center";
  const scale = Math.min(maxWMm / logo.w, maxHMm / logo.h);
  const dw = logo.w * scale;
  const dh = logo.h * scale;
  let dx = xMm;
  if (align === "center") dx = xMm + (maxWMm - dw) / 2;
  else if (align === "right") dx = xMm + (maxWMm - dw);
  const dy = yMm + (maxHMm - dh) / 2;
  pdf.addImage(logo.dataUrl, "PNG", dx, dy, dw, dh);
  return { w: dw, h: dh, x: dx, y: dy };
}

// ─── Color helpers ─────────────────────────────────────────────────
export function hexToRgb(hex) {
  if (!hex) return [255, 255, 255];
  const h = hex.replace("#", "");
  const n = h.length === 3
    ? h.split("").map((c) => c + c).join("")
    : h.padEnd(6, "0");
  return [
    parseInt(n.slice(0, 2), 16),
    parseInt(n.slice(2, 4), 16),
    parseInt(n.slice(4, 6), 16),
  ];
}

export function setFill(pdf, hex) {
  const [r, g, b] = hexToRgb(hex);
  pdf.setFillColor(r, g, b);
}

export function setStroke(pdf, hex) {
  const [r, g, b] = hexToRgb(hex);
  pdf.setDrawColor(r, g, b);
}

export function setText(pdf, hex) {
  const [r, g, b] = hexToRgb(hex);
  pdf.setTextColor(r, g, b);
}

export function safe(v, fallback = "") {
  if (v === null || v === undefined) return fallback;
  return String(v);
}

export function getAttrs(data) {
  // iter v28r8: NON includere attributi vuoti — niente "—" placeholder
  // imbarazzante. Se l'agente non ha messo bagni/piano/classe energetica,
  // il layout li nasconde e ribilancia la griglia (3 colonne).
  const all = [
    ["TIPOLOGIA", data.property_type],
    ["SUPERFICIE", data.surface ? `${data.surface} mq` : null],
    ["LOCALI", data.rooms ? String(data.rooms) : null],
    ["BAGNI", data.bathrooms ? String(data.bathrooms) : null],
    ["PIANO", data.floor],
    ["CLASSE EN.", data.energy_class],
  ];
  return all.filter(([_, v]) => v && String(v).trim() && String(v).trim() !== "-");
}

export function cleanCity(city) {
  if (!city) return "";
  let c = String(city).trim().replace(/\s*\([^)]+\)\s*$/, "").trim();
  const parts = c.split(/\s*[·,\-/]\s*/).map((s) => s.trim()).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1] : c;
}

/**
 * Ritorna la città SOLO se non è già contenuta nel titolo, per evitare
 * la ripetizione "MILANO" sopra/sotto il titolo "...Brera, MILANO".
 *
 * @param {object} data — oggetto da buildData
 * @returns {string} città da mostrare, oppure stringa vuota
 */
export function cityIfNotInTitle(data) {
  const city = cleanCity(data?.city);
  if (!city) return "";
  const title = (data?.title || "").toLowerCase();
  if (title.includes(city.toLowerCase())) return "";
  return city;
}

/**
 * Carica il logo agenzia. Se è monocromatico (data.agencyLogoIsMonochrome=true)
 * lo tinge con il colore richiesto ("white" su bg scuro, "black" su bg chiaro).
 * Se è colorato lo mantiene fedele all'originale.
 *
 * Wrapper su loadImageAsJpegDataUrl con tint conditional.
 */
export async function loadAgencyLogo(data, maxWidth, bgIsDark) {
  if (!data?.agencyLogoUrl) return null;
  // iter v28 r17 — sempre forcePng: il logo agency è SEMPRE caricato come PNG
  // trasparente sul bg della pagina (cover su foto, contatti su nero, ecc.).
  // Il flag mono inverte automaticamente bianco↔nero in base al bg via
  // composite source-in (preserva l'alpha del logo originale).
  const opts = {
    forcePng: true,
    ...(data.agencyLogoIsMonochrome ? { tint: bgIsDark ? "white" : "black" } : {}),
  };
  return loadImageAsJpegDataUrl(data.agencyLogoUrl, maxWidth, opts);
}

/**
 * Tronca email lunghe per evitare overflow del layout: se più lunghe di
 * `maxLen` caratteri, mostra `local…@domain.tld` (es. qa+1779185580brochure@vista.it
 * → qa+177…@vista.it).
 */
export function shortenEmail(email, maxLen = 32) {
  if (!email || email.length <= maxLen) return email;
  const [local, domain] = email.split("@");
  if (!domain) return email.slice(0, maxLen - 1) + "…";
  // Mantieni dominio intero, riduci la parte locale
  const room = maxLen - domain.length - 2; // 1 per @ + 1 per …
  if (room < 4) return email.slice(0, maxLen - 1) + "…";
  return `${local.slice(0, room)}…@${domain}`;
}
